import { z } from 'zod';

// ─── Pre Flight – Scripts (script-review) event payloads ─────────────
// Async pipeline: a user reviews a finished video creative against its
// production script; AI produces timestamped comments. The event rides
// the shared NEXUS.PREFLIGHT topic (PreflightEvents.SCRIPT_REVIEW_REQUESTED).

/**
 * Published by services/api when a review is requested. The preflight
 * worker (services/preflight) loads the video, script, and scenes from
 * the ScriptReview row identified by `reviewId`.
 */
export const ReviewRequestedPayload = z.object({
  reviewId: z.string().cuid2(),
  tenantId: z.string().cuid2(),
  submissionId: z.string().min(1).optional(),
  attemptVersion: z.string().min(1).optional(),
});

export type ReviewRequestedPayload = z.infer<typeof ReviewRequestedPayload>;

/**
 * Published by services/api when an AI comment is deleted WITH a reason. The
 * ticket row (ScriptReviewCommentRejection) is created synchronously and is the
 * source of truth — this event only triggers the worker's suggestion draft.
 * (Plain min(1) ids — not cuid2 — since Prisma generates cuid v1.)
 */
export const CommentRejectedPayload = z.object({
  rejectionId: z.string().min(1),
  tenantId: z.string().min(1),
});

export type CommentRejectedPayload = z.infer<typeof CommentRejectedPayload>;

/**
 * Published by services/api from the creation form: transcribe the uploaded
 * video and pick which script in `setId` it was made from. Nothing is persisted
 * — the worker writes the verdict to the correlation-status cache the form
 * polls, and the admin confirms (or overrides) the suggestion before running.
 */
export const ScriptMatchRequestedPayload = z.object({
  uploadId: z.string().min(1),
  setId: z.string().min(1),
  tenantId: z.string().min(1),
});

export type ScriptMatchRequestedPayload = z.infer<typeof ScriptMatchRequestedPayload>;

/**
 * Scene `hearing` is an unbounded text column. These caps are the contract
 * for the partner voiceover check so a stored script cannot publish an
 * oversized payload or feed an unbounded word-overlap pass. A long ad script
 * fits; the totals stay far under Pub/Sub's 10 MB message limit.
 */
export const PARTNER_VOICEOVER_LINE_MAX_COUNT = 200;
export const PARTNER_VOICEOVER_LINE_MAX_CHARS = 4_000;
export const PARTNER_VOICEOVER_LINES_MAX_CHARS = 64_000;

export const PartnerVoiceoverLinesSchema = z
  .array(
    z
      .string()
      .min(1)
      .max(
        PARTNER_VOICEOVER_LINE_MAX_CHARS,
        `Voiceover line must be ${PARTNER_VOICEOVER_LINE_MAX_CHARS} characters or fewer`,
      ),
  )
  .min(1)
  .max(
    PARTNER_VOICEOVER_LINE_MAX_COUNT,
    `Voiceover must be ${PARTNER_VOICEOVER_LINE_MAX_COUNT} lines or fewer`,
  )
  .refine(
    (lines) =>
      lines.reduce((total, line) => total + line.length, 0) <= PARTNER_VOICEOVER_LINES_MAX_CHARS,
    { message: `Voiceover text must be ${PARTNER_VOICEOVER_LINES_MAX_CHARS} characters or fewer` },
  );

/**
 * Published by services/api from the partner portal's pre-submit confirm
 * dialog: transcribe the partner's uploaded video and score its mismatch
 * against `voiceoverLines` (resolved by the API from the order line's script).
 * Nothing is persisted — the verdict lands in the correlation status the
 * dialog polls, and it is advisory only.
 */
export const PartnerVoiceoverCheckRequestedPayload = z.object({
  uploadId: z.string().min(1),
  /** The partner org that owns the upload (not the client org). */
  uploadTenantId: z.string().min(1),
  voiceoverLines: PartnerVoiceoverLinesSchema,
});

export type PartnerVoiceoverCheckRequestedPayload = z.infer<
  typeof PartnerVoiceoverCheckRequestedPayload
>;

/** The three comparison passes the worker runs, one Gemini call each. */
export const ScriptReviewCommentTypeEnum = z.enum([
  'VOICEOVER',
  'TEXT_OVERLAY',
  'VISUAL',
]);
export type ScriptReviewCommentType = z.infer<typeof ScriptReviewCommentTypeEnum>;

/**
 * What the video is judged against (mirrors the Prisma ScriptReviewMode enum).
 * SCRIPT = a production script's scenes; ITERATION = a previously successful
 * reference video + the change brief the vendor was given; CLEAN = stored with
 * no comparison, so it completes with zero AI comments.
 */
export const ScriptReviewModeEnum = z.enum(['SCRIPT', 'ITERATION', 'CLEAN']);
export type ScriptReviewMode = z.infer<typeof ScriptReviewModeEnum>;

export const ScriptReviewCommentSeverityEnum = z.enum([
  'issue',
  'discrepancy',
  'quality',
]);
export type ScriptReviewCommentSeverity = z.infer<
  typeof ScriptReviewCommentSeverityEnum
>;

/**
 * A single AI-produced note anchored to a video timestamp. `exactSecond`
 * is a non-negative number the worker clamps to the video duration before
 * persisting it for display in the in-app timeline player.
 */
export const ScriptReviewCommentSchema = z.object({
  type: ScriptReviewCommentTypeEnum,
  text: z.string().min(1),
  exactSecond: z.number().nonnegative(),
  sceneNumber: z.number().int().nullable(),
  severity: ScriptReviewCommentSeverityEnum.optional(),
});
export type ScriptReviewComment = z.infer<typeof ScriptReviewCommentSchema>;
